import {
  Box,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";

import { RangeType } from "@hooks/useChartRange";
import { StatusesType } from "@hooks/useFilteredEmails";

import theme from "@theme/index";

interface ICardHeaderProperties {
  activeRange?: RangeType;
  activeSection?: StatusesType;
  hasClicked?: boolean;
  hasOpened?: boolean;
  hasResponded?: boolean;
  setActiveRange?: (range: RangeType) => void;
  setActiveSection?: (section: StatusesType) => void;
  shouldShowDropdown?: boolean;
  shouldShowToggleButtons?: boolean;
  title: string;
}

export default function CardHeader({
  activeRange,
  activeSection,
  hasClicked = false,
  hasOpened = false,
  hasResponded = false,
  setActiveRange,
  setActiveSection,
  shouldShowDropdown = false,
  shouldShowToggleButtons = false,
  title,
}: ICardHeaderProperties) {
  return (
    <Box sx={{ alignItems: "center", display: "flex", height: "34px", justifyContent: "space-between" }}>
      <Typography fontWeight={700} variant="h6">
        {title}
      </Typography>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 2,
        }}
      >
        {shouldShowDropdown && (
          <FormControl
            fullWidth
            size="small"
            sx={{
              ".MuiInputBase-formControl": {
                width: "220px",
              },
            }}
            variant="outlined"
          >
            <Tooltip
              placement="bottom"
              title="Adjust the timescale to see Campaign engagement: first 24 hours or all time."
            >
              <Select onChange={(event) => setActiveRange?.(event.target.value as RangeType)} value={activeRange}>
                <MenuItem value={"24h"}>First 24 Hours</MenuItem>
                <MenuItem value={"allTime"}>All Time Events</MenuItem>
              </Select>
            </Tooltip>
          </FormControl>
        )}

        {shouldShowToggleButtons && (
          <ToggleButtonGroup
            sx={{
              [`& .${toggleButtonGroupClasses.disabled}`]: {
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[600]} !important`,
                color: `${theme.palette.action.disabled} !important`,
              },
              [`& .${toggleButtonGroupClasses.grouped}`]: {
                "&:hover": {
                  color: theme.palette.primary.main,
                },
                backgroundColor: theme.palette.common.white,
                color: theme.palette.primary.contrastText,
              },
              [`& .${toggleButtonGroupClasses.selected}`]: {
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                },
                backgroundColor: `${theme.palette.secondary.main} !important`,
                color: `${theme.palette.common.white} !important`,
              },
              height: "34px",
              transition: "all 0.1s ease-in-out",
            }}
            value={activeSection}
          >
            {/*- [ ]  opened: `View contacts who opened your email.`*/}
            {/*- [ ]  responded: `View contacts who responded your email.`*/}
            {/*- [ ]  clicked: `View contacts who clicked a link in your email.`*/}
            <Tooltip placement="bottom" title="View contacts who opened your email.">
              <ToggleButton disabled={!hasOpened} onClick={(_, value) => setActiveSection?.(value)} value={"opened"}>
                Opened
              </ToggleButton>
            </Tooltip>

            <Tooltip placement="bottom" title="View contacts who clicked a link in your email.">
              <ToggleButton disabled={!hasClicked} onClick={(_, value) => setActiveSection?.(value)} value={"clicked"}>
                Clicked
              </ToggleButton>
            </Tooltip>
            <Tooltip placement="bottom" title="View contacts who responded to your email.">
              <ToggleButton
                disabled={!hasResponded}
                onClick={(_, value) => setActiveSection?.(value)}
                value={"responded"}
              >
                Responded
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        )}
      </Box>
    </Box>
  );
}
